import React, { useState, useRef } from 'react';
import { Download, Upload, User, Mail, Phone, Hash, Briefcase, Trash2 } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const IDCardPortal = () => {
  const initialState = {
    name: '',
    designation: '',
    mobile: '',
    email: '',
    id: '',
    photo: null,
    logo: null,
  };

  const [formData, setFormData] = useState(initialState);
  const frontRef = useRef();
  const backRef = useRef();

  const handleUpload = (e, field) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setFormData({ ...formData, [field]: reader.result });
      reader.readAsDataURL(file);
    }
  };

  const resetForm = () => setFormData(initialState);

  const downloadPDF = async () => {
    // Standard CR80 ID Card dimensions: 54mm x 86mm
    const pdfWidth = 54;
    const pdfHeight = 86;
    const pdf = new jsPDF('p', 'mm', [pdfWidth, pdfHeight]);

    const capture = async (ref) => {
      const element = ref.current;
      
      // We use a high scale (3) for crispness but limit the width/height
      // to the actual element's size to prevent "auto-cropping"
      const canvas = await html2canvas(element, { 
        scale: 3, 
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        scrollX: 0,
        scrollY: -window.scrollY, // Fixes misalignment if user has scrolled down
        width: element.offsetWidth,
        height: element.offsetHeight
      });
      return canvas.toDataURL('image/png', 1.0);
    };

    try {
      // Capture Front Card
      const frontImg = await capture(frontRef);
      pdf.addImage(frontImg, 'PNG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
      
      // Add Back Card on second page
      pdf.addPage([pdfWidth, pdfHeight], 'p');
      const backImg = await capture(backRef);
      pdf.addImage(backImg, 'PNG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');

      pdf.save(`${formData.name || 'DailyGo'}_ID_Card.pdf`);
    } catch (err) {
      console.error("PDF generation failed:", err);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f172a] flex flex-col lg:flex-row font-sans">
      {/* SIDEBAR FORM */}
      <div className="w-full lg:w-[400px] bg-[#1e293b] p-8 shadow-2xl z-10 overflow-y-auto border-r border-slate-700">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <div className="w-2 h-8 bg-yellow-500 rounded-full"></div>
            Daily Go Portal
          </h2>
          <button onClick={resetForm} className="text-slate-400 hover:text-red-400 transition-colors">
            <Trash2 size={20} />
          </button>
        </div>

        <div className="space-y-5">
          <InputGroup label="Full Name" icon={<User size={18}/>} placeholder="Hanish S.S" 
            value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} />
          
          <InputGroup label="Designation" icon={<Briefcase size={18}/>} placeholder="Developer" 
            value={formData.designation} onChange={(e) => setFormData({...formData, designation: e.target.value})} />

          <InputGroup label="Mobile Number" icon={<Phone size={18}/>} placeholder="+91 00000 00000" 
            value={formData.mobile} onChange={(e) => setFormData({...formData, mobile: e.target.value})} />

          <InputGroup label="Email ID" icon={<Mail size={18}/>} placeholder="office@dailygo.in" 
            value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />

          <InputGroup label="Daily Go ID" icon={<Hash size={18}/>} placeholder="DG-DP-2025" 
            value={formData.id} onChange={(e) => setFormData({...formData, id: e.target.value})} />

          <div className="grid grid-cols-2 gap-4 pt-2">
            <div>
              <label className="block text-[10px] font-bold text-slate-400 uppercase mb-2">User Photo</label>
              <label className="flex flex-col items-center justify-center h-20 bg-[#0f172a] border-2 border-dashed border-slate-700 rounded-xl cursor-pointer hover:border-yellow-500 transition-all overflow-hidden">
                {formData.photo ? <img src={formData.photo} className="w-full h-full object-cover" alt="prev" /> : <Upload size={20} className="text-slate-500" />}
                <input type="file" className="hidden" onChange={(e) => handleUpload(e, 'photo')} />
              </label>
            </div>
            <div>
              <label className="block text-[10px] font-bold text-slate-400 uppercase mb-2">Company Logo</label>
              <label className="flex flex-col items-center justify-center h-20 bg-[#0f172a] border-2 border-dashed border-slate-700 rounded-xl cursor-pointer hover:border-yellow-500 transition-all overflow-hidden">
                {formData.logo ? <img src={formData.logo} className="w-full h-full object-contain p-2" alt="prev" /> : <Upload size={20} className="text-slate-500" />}
                <input type="file" className="hidden" onChange={(e) => handleUpload(e, 'logo')} />
              </label>
            </div>
          </div>

          <button onClick={downloadPDF} className="w-full mt-6 bg-yellow-500 text-black py-4 rounded-xl font-black flex items-center justify-center gap-2 hover:bg-yellow-400 transition-all shadow-[0_0_20px_rgba(234,179,8,0.2)]">
            <Download size={20} /> DOWNLOAD PDF
          </button>
        </div>
      </div>

      {/* PREVIEW AREA */}
      <div className="flex-1 p-10 flex flex-col md:flex-row items-center justify-center gap-12 bg-[#0f172a] overflow-auto">
        
        {/* FRONT CARD */}
        <div ref={frontRef} className="w-[350px] h-[550px] bg-white rounded-[32px] shadow-2xl relative overflow-hidden flex flex-col items-center p-8 flex-shrink-0 box-border border border-gray-100">
          <div className="absolute top-0 right-0 w-full h-24 bg-gradient-to-l from-yellow-500 via-yellow-400 to-transparent opacity-10 rotate-12 -translate-y-10 translate-x-10"></div>
          <div className="absolute top-0 left-0 w-full h-4 bg-black"></div>
          <div className="absolute top-4 left-0 w-full h-2 bg-yellow-500"></div>

          <div className="h-16 mt-4 flex items-center justify-center">
            {formData.logo ? <img src={formData.logo} className="max-h-full" alt="logo"/> : <span className="font-black text-2xl italic tracking-tighter text-black">DAILY <span className="text-yellow-500">GO</span></span>}
          </div>
          <p className="text-[10px] uppercase tracking-widest text-gray-400 font-bold mb-8">Your Trusted Source For Quality</p>

          <div className="relative mb-6">
            <div className="w-40 h-40 rounded-full border-[6px] border-yellow-500 p-1 bg-white">
              <div className="w-full h-full rounded-full overflow-hidden bg-gray-100">
                {formData.photo ? <img src={formData.photo} className="w-full h-full object-cover" alt="p" /> : <div className="w-full h-full flex items-center justify-center text-gray-300"><User size={60}/></div>}
              </div>
            </div>
          </div>

          <div className="text-center flex-1 w-full">
            <h1 className="text-2xl font-black text-gray-800 uppercase leading-tight mb-1">{formData.name || 'Hanish S.S'}</h1>
            <p className="text-yellow-600 font-bold text-sm tracking-widest uppercase mb-6 italic">{formData.designation || 'Developer'} @ Daily Go Pvt Ltd</p>
            
            <div className="space-y-1 text-sm text-gray-600 font-medium">
              <p>{formData.mobile || '+91 00000 00000'}</p>
              <p className="lowercase">{formData.email || 'office@dailygo.in'}</p>
              <div className="mt-4 py-2 px-6 bg-black text-yellow-500 rounded-full font-bold inline-block tracking-widest text-xs">
                ID: {formData.id || 'DG-XXXX'}
              </div>
            </div>
          </div>

          <div className="w-full border-t border-dashed border-gray-200 pt-4 text-center mt-auto">
            <p className="text-xs font-bold text-gray-400">Visit: dailygolive.in</p>
          </div>
        </div>

        {/* BACK CARD */}
        <div ref={backRef} className="w-[350px] h-[550px] bg-white rounded-[32px] shadow-2xl relative overflow-hidden flex flex-col p-10 flex-shrink-0 box-border border border-gray-100">
          <div className="absolute top-0 left-0 w-full h-4 bg-black"></div>
          <div className="absolute top-4 left-0 w-full h-2 bg-yellow-500"></div>

          <div className="mt-8 mb-6">
             <h3 className="font-black text-xl text-gray-800 uppercase">DAILY GO PVT LTD</h3>
             <p className="text-[11px] font-bold text-gray-500 leading-tight mt-1">Registered Office: No. 6, Gubbala Main Road, BWSSB Pipeline, Subramanyapura, Bengaluru - 560062.</p>
          </div>

          <div className="space-y-6 flex-1">
            <section>
                <h4 className="text-[10px] font-black uppercase text-yellow-600 mb-2 tracking-widest">Contact Details</h4>
                <div className="text-xs space-y-1 font-bold text-gray-700">
                    <p>Email: <span className="font-medium">office-info@dailygolive.in</span></p>
                    <p>Phone: <span className="font-medium">+91 9739777166</span></p>
                    <p>Web: <span className="font-medium font-mono">dailygolive.in</span></p>
                </div>
            </section>

            <section className="bg-gray-50 p-4 rounded-xl border-l-4 border-yellow-500">
                <p className="text-[10px] leading-relaxed italic text-gray-600 font-medium">
                "Daily Go simplifies bulk buying by offering transparent pricing, better margins, and seamless business connections."
                </p>
            </section>

            <section className="text-[9px] text-gray-400 font-bold uppercase leading-tight">
                This card is company property and is non-transferable. If found, kindly return to the company office.
            </section>

            <ul className="text-[10px] text-gray-700 space-y-1 list-disc pl-4 font-bold">
                <li>Must be carried during duty hours.</li>
                <li>Loss must be reported immediately.</li>
            </ul>
          </div>

          <div className="mt-4 flex flex-col items-end">
            <div className="w-24 border-b-2 border-gray-800 mb-1"></div>
            <p className="text-[10px] font-black uppercase text-gray-800">Authorized Signature</p>
          </div>
          
          <div className="absolute bottom-0 left-0 w-full h-2 bg-black"></div>
        </div>
      </div>
    </div>
  );
};

const InputGroup = ({ label, icon, ...props }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[10px] font-bold text-slate-400 uppercase ml-1 tracking-wider">{label}</label>
    <div className="relative group">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500 group-focus-within:text-yellow-500 transition-colors">
        {icon}
      </div>
      <input 
        {...props}
        className="block w-full pl-10 pr-3 py-3 border border-slate-700 rounded-xl leading-5 bg-[#0f172a] text-white placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-yellow-500 focus:border-yellow-500 transition-all text-sm"
      />
    </div>
  </div>
);

export default IDCardPortal;